// SPDX-License-Identifier: Apache-2.0
// File: src/audio/ChordLibrary.ts

export interface SmartChord {
  id: number; // 0..7
  label: string; // e.g. "Dm9", "G13", "Cmaj9"
  root: string; // e.g. "D"
  type: string; // e.g. "m9"
  voicing: 'standard' | 'open' | 'drop2' | 'quartal';
  offsets: number[]; // semitone offsets from root (0 = root)
}

export interface ChordTypeDefinition {
  id: string;
  name: string;
  shortLabel: string;
  category: 'MAJOR' | 'MINOR' | 'DOMINANT' | 'SUSPENDED' | 'DIM_AUG' | 'EXTENDED';
  baseOffsets: number[];
  description: string;
}

export const CHORD_ROOTS = [
  'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'
] as const;

export type ChordRootName = typeof CHORD_ROOTS[number];

export const ROOT_SEMITONES: Record<ChordRootName, number> = {
  'C': 0, 'C#': 1, 'D': 2, 'D#': 3, 'E': 4, 'F': 5,
  'F#': 6, 'G': 7, 'G#': 8, 'A': 9, 'A#': 10, 'B': 11,
};

export const CHORD_CATEGORIES = [
  { id: 'MAJOR', label: 'MAJOR' },
  { id: 'MINOR', label: 'MINOR' },
  { id: 'DOMINANT', label: 'DOMINANT' },
  { id: 'SUSPENDED', label: 'SUSPENDED' },
  { id: 'EXTENDED', label: '9 / 11 / 13' },
  { id: 'DIM_AUG', label: 'DIM / AUG' },
] as const;

export const CHORD_TYPE_DEFINITIONS: ChordTypeDefinition[] = [
  // --- MAJOR ---
  { id: 'Maj', name: 'Major Triad', shortLabel: '', category: 'MAJOR', baseOffsets: [0, 4, 7], description: 'Pure, consonant, bright' },
  { id: 'Maj7', name: 'Major 7th', shortLabel: 'maj7', category: 'MAJOR', baseOffsets: [0, 4, 7, 11], description: 'Dreamy, smooth, sophisticated' },
  { id: '6', name: 'Major 6th', shortLabel: '6', category: 'MAJOR', baseOffsets: [0, 4, 7, 9], description: 'Classic swing, vintage warm' },
  { id: '6/9', name: 'Six-Nine', shortLabel: '6/9', category: 'MAJOR', baseOffsets: [0, 4, 7, 9, 14], description: 'Bossa nova, lush shimmer' },
  { id: 'Add9', name: 'Major Add 9', shortLabel: 'add9', category: 'MAJOR', baseOffsets: [0, 4, 7, 14], description: 'Airy, open, modern pop' },
  { id: 'Maj7#11', name: 'Major 7 (#11)', shortLabel: 'maj7#11', category: 'MAJOR', baseOffsets: [0, 4, 7, 11, 18], description: 'Lydian cinematic mysticism' },

  // --- MINOR ---
  { id: 'Min', name: 'Minor Triad', shortLabel: 'm', category: 'MINOR', baseOffsets: [0, 3, 7], description: 'Moody, grounded, somber' },
  { id: 'Min7', name: 'Minor 7th', shortLabel: 'm7', category: 'MINOR', baseOffsets: [0, 3, 7, 10], description: 'Classic R&B, deep house foundation' },
  { id: 'Min6', name: 'Minor 6th', shortLabel: 'm6', category: 'MINOR', baseOffsets: [0, 3, 7, 9], description: 'Noir detective, melancholic' },
  { id: 'Min(maj7)', name: 'Minor (Major 7)', shortLabel: 'm(maj7)', category: 'MINOR', baseOffsets: [0, 3, 7, 11], description: 'James Bond, tension thriller' },
  { id: 'm(add9)', name: 'Minor Add 9', shortLabel: 'm(add9)', category: 'MINOR', baseOffsets: [0, 3, 7, 14], description: 'Haunting, introspective' },

  // --- DOMINANT ---
  { id: '7', name: 'Dominant 7th', shortLabel: '7', category: 'DOMINANT', baseOffsets: [0, 4, 7, 10], description: 'Blues grit, driving resolve' },
  { id: '9', name: 'Dominant 9th', shortLabel: '9', category: 'DOMINANT', baseOffsets: [0, 4, 7, 10, 14], description: 'Funk, soul, groove staple' },
  { id: '13', name: 'Dominant 13th', shortLabel: '13', category: 'DOMINANT', baseOffsets: [0, 4, 10, 14, 21], description: 'Lush Motown, bebop elegance' },
  { id: '7#9', name: 'Hendrix 7(#9)', shortLabel: '7#9', category: 'DOMINANT', baseOffsets: [0, 4, 7, 10, 15], description: 'Psychedelic grit, raw bite' },
  { id: '7b9', name: 'Dominant 7(b9)', shortLabel: '7b9', category: 'DOMINANT', baseOffsets: [0, 4, 7, 10, 13], description: 'Flamenco, dark jazz resolution' },
  { id: '7alt', name: 'Altered Dominant', shortLabel: '7alt', category: 'DOMINANT', baseOffsets: [0, 4, 10, 13, 15], description: 'Super-locrian fusion pivot' },

  // --- SUSPENDED & OPEN ---
  { id: 'Sus2', name: 'Suspended 2nd', shortLabel: 'sus2', category: 'SUSPENDED', baseOffsets: [0, 2, 7], description: 'Spacious, atmospheric, floating' },
  { id: 'Sus4', name: 'Suspended 4th', shortLabel: 'sus4', category: 'SUSPENDED', baseOffsets: [0, 5, 7], description: 'Classic rock tension, anticipation' },
  { id: '7sus4', name: '7th Suspended 4th', shortLabel: '7sus4', category: 'SUSPENDED', baseOffsets: [0, 5, 7, 10], description: 'Gospel anthem, soaring lift' },
  { id: 'Power5', name: 'Power Chord (1-5-8)', shortLabel: '5', category: 'SUSPENDED', baseOffsets: [0, 7, 12], description: 'Raw, heavy, neutral punch' },

  // --- EXTENDED (NEO-SOUL & JAZZ) ---
  { id: 'Maj9', name: 'Major 9th', shortLabel: 'maj9', category: 'EXTENDED', baseOffsets: [0, 4, 7, 11, 14], description: 'Silky, warm, contemporary soul' },
  { id: 'm9', name: 'Minor 9th', shortLabel: 'm9', category: 'EXTENDED', baseOffsets: [0, 3, 7, 10, 14], description: 'Essential Neo-Soul & Lo-Fi staple' },
  { id: 'm11', name: 'Minor 11th', shortLabel: 'm11', category: 'EXTENDED', baseOffsets: [0, 7, 10, 14, 17], description: 'Deep, cloudy, modern jazz spread' },
  { id: 'Maj11', name: 'Major 11th', shortLabel: 'maj11', category: 'EXTENDED', baseOffsets: [0, 4, 7, 11, 14, 17], description: 'Rich ethereal landscape' },
  { id: 'Maj13', name: 'Major 13th', shortLabel: 'maj13', category: 'EXTENDED', baseOffsets: [0, 4, 11, 14, 21], description: 'Peak harmonic luxury' },
  { id: 'm13', name: 'Minor 13th', shortLabel: 'm13', category: 'EXTENDED', baseOffsets: [0, 3, 10, 14, 21], description: 'Complex modern fusion' },

  // --- DIMINISHED & AUGMENTED ---
  { id: 'm7b5', name: 'Half-Diminished (m7b5)', shortLabel: 'm7b5', category: 'DIM_AUG', baseOffsets: [0, 3, 6, 10], description: 'Minor ii-V pivot, tender tension' },
  { id: 'Dim7', name: 'Diminished 7th', shortLabel: 'dim7', category: 'DIM_AUG', baseOffsets: [0, 3, 6, 9], description: 'Symmetrical passing chord, tension' },
  { id: 'Aug', name: 'Augmented Triad', shortLabel: 'aug', category: 'DIM_AUG', baseOffsets: [0, 4, 8], description: 'Spacey, whole-tone mystery' },
  { id: 'Aug7', name: 'Augmented 7th', shortLabel: 'aug7', category: 'DIM_AUG', baseOffsets: [0, 4, 8, 10], description: 'Bebop whole-tone push' },
];

export function applyVoicing(offsets: number[], voicing: SmartChord['voicing']): number[] {
  if (offsets.length < 3) return [...offsets];
  switch (voicing) {
    case 'open': {
      // Drop the root down one octave for a warm, wide foundation
      const sorted = [...offsets].sort((a, b) => a - b);
      return [sorted[0] - 12, ...sorted.slice(1)];
    }
    case 'drop2': {
      // Drop second-highest note by one octave (classic jazz keyboard voicing)
      const sorted = [...offsets].sort((a, b) => a - b);
      if (sorted.length >= 4) {
        const dropIdx = sorted.length - 2;
        const droppedNote = sorted[dropIdx] - 12;
        const rest = sorted.filter((_, idx) => idx !== dropIdx);
        return [droppedNote, ...rest].sort((a, b) => a - b);
      }
      return [sorted[0] - 12, ...sorted.slice(1)];
    }
    case 'quartal': {
      // Stack intervals in 4ths for modern modal sound
      return offsets.map((val, idx) => (idx % 2 === 1 ? val + 5 : val));
    }
    case 'standard':
    default:
      return [...offsets];
  }
}

export function buildSmartChord(
  slotId: number,
  root: ChordRootName,
  typeId: string,
  voicing: SmartChord['voicing'] = 'open'
): SmartChord {
  const def = CHORD_TYPE_DEFINITIONS.find((d) => d.id === typeId) || CHORD_TYPE_DEFINITIONS[0];
  const rootOffset = ROOT_SEMITONES[root] ?? 0;
  // Transpose relative to D3 (MIDI 62 standard reference)
  // Shift so root aligns correctly
  const baseOffsets = def.baseOffsets.map((o) => o + rootOffset - 2); // -2 centers around D
  const voicedOffsets = applyVoicing(baseOffsets, voicing);

  const label = `${root}${def.shortLabel}`;

  return {
    id: slotId,
    label,
    root,
    type: typeId,
    voicing,
    offsets: voicedOffsets,
  };
}

export interface ChordPresetPack {
  id: string;
  name: string;
  badge: string;
  description: string;
  chords: { root: ChordRootName; type: string; voicing: SmartChord['voicing'] }[];
}

export const CHORD_PRESET_PACKS: ChordPresetPack[] = [
  {
    id: 'neo_soul_groove',
    name: 'Neo-Soul Smooth',
    badge: 'SMOOTH & LUSH',
    description: 'Silky, rich 9ths & 13ths with warm open bass voicings',
    chords: [
      { root: 'D', type: 'm9', voicing: 'open' },
      { root: 'G', type: '13', voicing: 'open' },
      { root: 'C', type: 'Maj9', voicing: 'open' },
      { root: 'A', type: 'm11', voicing: 'open' },
      { root: 'F', type: 'Maj9', voicing: 'open' },
      { root: 'E', type: 'm11', voicing: 'open' },
      { root: 'A#', type: 'Maj7', voicing: 'open' }, // Bb represented as A#
      { root: 'A', type: '7#9', voicing: 'open' },
    ],
  },
  {
    id: 'house_anthem',
    name: 'House & EDM Anthems',
    badge: 'CLUB STABS',
    description: 'Pumping minor 7ths, soaring suspensions, and festival hooks',
    chords: [
      { root: 'A', type: 'Min7', voicing: 'standard' },
      { root: 'F', type: 'Maj7', voicing: 'standard' },
      { root: 'C', type: 'Maj', voicing: 'standard' },
      { root: 'G', type: '7', voicing: 'standard' },
      { root: 'D', type: 'Min7', voicing: 'standard' },
      { root: 'E', type: 'Min7', voicing: 'standard' },
      { root: 'A#', type: 'Maj7', voicing: 'standard' },
      { root: 'E', type: '7sus4', voicing: 'standard' },
    ],
  },
  {
    id: 'lofi_chill',
    name: 'Lo-Fi Chillhop',
    badge: 'MIDNIGHT COFFEE',
    description: 'Dusty vintage voicings with soft m9s and altered tension',
    chords: [
      { root: 'C', type: 'm9', voicing: 'drop2' },
      { root: 'F', type: '13', voicing: 'drop2' },
      { root: 'A#', type: 'Maj9', voicing: 'drop2' },
      { root: 'G', type: 'm11', voicing: 'drop2' },
      { root: 'G#', type: 'Maj7#11', voicing: 'drop2' },
      { root: 'D', type: 'm7b5', voicing: 'drop2' },
      { root: 'G', type: '7alt', voicing: 'drop2' },
      { root: 'D#', type: 'Maj9', voicing: 'drop2' },
    ],
  },
  {
    id: 'jazz_standards',
    name: 'Jazz Drop-2 Standards',
    badge: 'BLUE NOTE',
    description: 'Bebop ii-V-I progressions with voice-leading Drop-2 spreads',
    chords: [
      { root: 'D', type: 'Min7', voicing: 'drop2' },
      { root: 'G', type: '7', voicing: 'drop2' },
      { root: 'C', type: 'Maj7', voicing: 'drop2' },
      { root: 'A', type: '7b9', voicing: 'drop2' },
      { root: 'F', type: 'Min7', voicing: 'drop2' },
      { root: 'A#', type: '7', voicing: 'drop2' },
      { root: 'D#', type: 'Maj7', voicing: 'drop2' },
      { root: 'G#', type: '13', voicing: 'drop2' },
    ],
  },
  {
    id: 'pop_magic',
    name: 'Pop 4-Chord & Beyond',
    badge: 'RADIO HIT',
    description: 'Universal crowd-pleasers: I-V-vi-IV with modern add9 swells',
    chords: [
      { root: 'C', type: 'Maj', voicing: 'standard' },
      { root: 'G', type: 'Maj', voicing: 'standard' },
      { root: 'A', type: 'Min', voicing: 'standard' },
      { root: 'F', type: 'Maj', voicing: 'standard' },
      { root: 'D', type: 'Min', voicing: 'standard' },
      { root: 'E', type: 'Min', voicing: 'standard' },
      { root: 'F', type: 'Sus2', voicing: 'standard' },
      { root: 'G', type: 'Sus4', voicing: 'standard' },
    ],
  },
  {
    id: 'ambient_cinematic',
    name: 'Ambient Cinematic',
    badge: 'BLADE RUNNER',
    description: 'Wide suspended vistas, Lydian #11s, and floating textures',
    chords: [
      { root: 'C', type: 'Maj9', voicing: 'open' },
      { root: 'F', type: 'Maj7#11', voicing: 'open' },
      { root: 'A', type: 'm9', voicing: 'open' },
      { root: 'G', type: '6/9', voicing: 'open' },
      { root: 'D', type: 'Sus2', voicing: 'open' },
      { root: 'E', type: 'Add9', voicing: 'open' },
      { root: 'G#', type: 'Maj9', voicing: 'open' },
      { root: 'A#', type: 'Add9', voicing: 'open' },
    ],
  },
  {
    id: 'synthwave_dark',
    name: 'Dark Synthwave / Cyber',
    badge: 'OUTRUN 80s',
    description: 'Arpeggio-ready minor triads, heavy suspensions, and punchy power chords',
    chords: [
      { root: 'D', type: 'Min', voicing: 'open' },
      { root: 'A#', type: 'Maj', voicing: 'open' },
      { root: 'G', type: 'Min', voicing: 'open' },
      { root: 'A', type: 'Power5', voicing: 'open' },
      { root: 'F', type: 'Maj', voicing: 'open' },
      { root: 'C', type: 'Min', voicing: 'open' },
      { root: 'D#', type: 'Maj', voicing: 'open' },
      { root: 'D', type: 'Sus4', voicing: 'open' },
    ],
  },
];

const CHORD_STORAGE_KEY = 'pocketgrid_smart_chords_v2';

export function loadSavedSmartChords(): SmartChord[] {
  try {
    const raw = localStorage.getItem(CHORD_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length === 8) {
        return parsed;
      }
    }
  } catch (err) {
    console.warn('Failed to load saved smart chords:', err);
  }
  return getDefaultSmartChords();
}

export function saveSmartChordsToStorage(chords: SmartChord[]): void {
  try {
    localStorage.setItem(CHORD_STORAGE_KEY, JSON.stringify(chords));
  } catch (err) {
    console.warn('Failed to save smart chords to storage:', err);
  }
}

export function getDefaultSmartChords(): SmartChord[] {
  const pack = CHORD_PRESET_PACKS[0]; // Neo-Soul Smooth by default
  return pack.chords.map((c, idx) => buildSmartChord(idx, c.root, c.type, c.voicing));
}
